import { getConfig } from "../constants/config";
import { getTokenIn } from "../helpers/getTokenIn";
import { getTotalAmountIn } from "../helpers/getTotalAmountIn";
import { parseUnits } from "viem";
import { symphonyAddress } from "../../common/constants/contract";
import { verifyWalletClient } from "../helpers/verifyWalletClient";
import { erc20Abi } from "viem";
import tokenService from "../../common/tokenService";

/**
 * Approves the Symphony contract to spend tokens on behalf of the user using viem.
 * Supports both direct token approvals and route-based approvals.
 * Simulates the transaction before sending and waits for confirmations.
 *
 * @param {Object} params - Approval parameters
 * @param {(string|Array)} params.variable - Token address or route array
 *   - If string: Token address to approve (e.g., "0x123...")
 *   - If Array: Route array from getRoute
 * @param {import('viem').WalletClient} params.walletClient - Wallet client for approval transaction
 * @param {(string|bigint)} [params.amount] - Amount to approve
 *   Required when variable is a token address
 *   - If isRaw=true: Amount in raw units (wei)
 *   - If isRaw=false: Amount in formatted units (e.g., "1.0")
 * @param {Object} [params.options] - Additional options
 * @param {boolean} [params.options.isRaw=true] - If true, amount is in raw units
 * @param {number} [params.options.chainId=config.chainId] - Chain ID for approval
 * @param {number} [params.options.wait=1] - Number of block confirmations to wait
 *
 * @returns {Promise<import('viem').TransactionReceipt>} Transaction receipt of the approval
 *
 * @throws {Error} If amount or token address is missing
 * @throws {Error} If token is not listed on Symphony (when using formatted amounts)
 * @throws {Error} If approval transaction fails or simulation fails
 *
 * @example
 * // Example 1: Approve tokens for a route
 * const route = await getRoute({
 *   tokenIn: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   tokenOut: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
 *   amount: "1.0"
 * });
 * const receipt = await giveApproval({
 *   variable: route,
 *   walletClient: walletClient
 * });
 *
 * @example
 * // Example 2: Approve specific token amount with custom confirmations
 * const receipt = await giveApproval({
 *   variable: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   walletClient: walletClient,
 *   amount: 1000000000000000000n, // 1 WSEI in wei
 *   options: {
 *     isRaw: true,
 *     wait: 3 // Wait for 3 block confirmations
 *   }
 * });
 *
 * @example
 * // Example 3: Approve with formatted amount
 * const receipt = await giveApproval({
 *   variable: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   walletClient: walletClient,
 *   amount: "1.0", // 1 WSEI
 *   options: { isRaw: false }
 * });
 */
export const giveApproval = async ({
  variable, // tokenAddress or route
  walletClient = undefined,
  amount,
  options = {
    isRaw: true,
    chainId: getConfig().chainId,
    wait: 1,
  },
}) => {
  let amountToApprove;
  let tokenToApprove;

  // Get config for non-token data
  const config = getConfig();
  const {
    publicClient,
    chainId,
    additionalTokens,
    overrideDefaultTokens,
    nativeAddress,
  } = config;

  // Get updated token list with user's configuration applied
  const tokens = await tokenService.getAsyncTokenList(
    chainId,
    additionalTokens,
    overrideDefaultTokens,
    nativeAddress
  );

  // If route given as variable
  if (Array.isArray(variable) && variable.length !== 0) {
    // Use provided amount if specified, otherwise extract from route
    if (amount !== undefined) {
      // When amount is provided, we need to determine if it's raw or formatted
      if (options.isRaw) {
        amountToApprove = amount;
      } else {
        // For formatted amount, we need token info to get decimals
        const tokenInAddress = getTokenIn(variable);
        const tokenInLower = tokenInAddress.toLowerCase();
        if (!tokens[tokenInLower]) {
          throw new Error(
            `Token ${tokenInAddress} data or decimals missing. This might mean token is not listed on Symphony.`
          );
        }
        amountToApprove = parseUnits(
          amount,
          tokens[tokenInLower].attributes.decimals
        );
      }
    } else {
      // Use route's amount when no explicit amount provided
      amountToApprove = (await getTotalAmountIn(variable)).amountIn;
    }
    tokenToApprove = getTokenIn(variable);
  }

  // If tokenAddress is given as a variable
  if (
    !Array.isArray(variable) &&
    typeof variable === "string" &&
    variable.startsWith("0x") &&
    variable.length === 42
  ) {
    tokenToApprove = variable.toLowerCase();
    if (options.isRaw) amountToApprove = amount;
    if (!options.isRaw) {
      if (!tokens[tokenToApprove])
        throw new Error(
          `Token ${variable} data or decimals missing. This might mean token is not listed on Symphony.`
        );
      amountToApprove = parseUnits(
        amount,
        tokens[tokenToApprove].attributes.decimals
      );
    }
  }

  if (!amountToApprove || !tokenToApprove)
    throw new Error(
      `Amount or token address is missing to approve. Check params passed to giveApproval. amountToApprove: ${amountToApprove},tokenToApprove: ${tokenToApprove}`
    );

  try {
    const verifiedWalletClient = await verifyWalletClient(walletClient);

    const simulation = await publicClient.simulateContract({
      account: verifiedWalletClient.account,
      address: tokenToApprove,
      abi: erc20Abi,
      functionName: "approve",
      args: [symphonyAddress, amountToApprove],
    });

    const transaction = await verifiedWalletClient.writeContract(
      simulation.request
    );
    const receipt = await publicClient.waitForTransactionReceipt({
      confirmations: options.wait,
      hash: transaction,
    });
    if (receipt.status !== "success") {
      throw new Error("Approval transaction failed!");
    }
    return receipt;
  } catch (error) {
    console.error("Approval failed:", error.message);
    throw new Error("Approve transaction failed.");
  }
};
